import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Search, 
  Filter, 
  Plus, 
  RefreshCw, 
  Download,
  List,
  Grid,
  Calendar,
  User,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import { taskService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganisation } from '../../contexts/OrganisationContext';
import TaskList from './TaskList';
import TaskFilters from './TaskFilters';
import TaskCreateModal from './TaskCreateModal';
import TaskStatsCards from './TaskStatsCards';
import toast from 'react-hot-toast';

/**
 * TaskDashboard - Main task management interface
 * Features: List view, filtering, search, task creation, and statistics
 */
const TaskDashboard = () => {
  const { user } = useAuth();
  const { organisationId, engagementId } = useOrganisation();
  const scopeKey = `${organisationId || ''}:${engagementId || ''}`;
  const currentScopeRef = useRef(scopeKey);
  const taskRequestRef = useRef(0);
  const statsRequestRef = useRef(0);
  currentScopeRef.current = scopeKey;
  
  // State management
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentView, setCurrentView] = useState('list'); // list, kanban, calendar, my-tasks
  const [taskStats, setTaskStats] = useState(null);
  
  // Filters state
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assignee_id: '',
    control_id: '',
    assessment_id: '',
    limit: 50,
    offset: 0,
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
    hasMore: false
  });

  /**
   * Fetch tasks based on current filters and search
   */
  const fetchTasks = useCallback(async (resetOffset = false) => {
    const requestScope = scopeKey;
    const requestId = ++taskRequestRef.current;
    if (!organisationId || !engagementId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      const currentFilters = {
        ...filters,
        offset: resetOffset ? 0 : filters.offset,
        search: searchTerm.trim() || undefined
      };

      // Handle special views
      if (currentView === 'my-tasks' && user?.id) {
        currentFilters.assignee_id = user.id;
      }

      const response = await taskService.getTasks(currentFilters);
      if (currentScopeRef.current !== requestScope || taskRequestRef.current !== requestId) return;
      
      if (resetOffset || filters.offset === 0) {
        setTasks(response.tasks || []);
      } else {
        // Append for pagination
        setTasks(prev => [...prev, ...(response.tasks || [])]);
      }
      
      setPagination(response.pagination || {});
      
    } catch (err) {
      if (currentScopeRef.current !== requestScope || taskRequestRef.current !== requestId) return;
      console.error('Failed to fetch tasks:', err);
      setError(err.message);
      toast.error(`Failed to load tasks: ${err.message}`);
    } finally {
      if (currentScopeRef.current === requestScope && taskRequestRef.current === requestId) setLoading(false);
    }
  }, [filters, searchTerm, currentView, user?.id, organisationId, engagementId, scopeKey]);

  /**
   * Fetch task statistics
   */
  const fetchTaskStats = useCallback(async () => {
    const requestScope = scopeKey;
    const requestId = ++statsRequestRef.current;
    if (!organisationId || !engagementId) return;
    try {
      const stats = await taskService.getTaskStats();
      if (currentScopeRef.current === requestScope && statsRequestRef.current === requestId) setTaskStats(stats);
    } catch (err) {
      if (currentScopeRef.current === requestScope && statsRequestRef.current === requestId) console.warn('Failed to fetch task stats:', err);
    }
  }, [organisationId, engagementId, scopeKey]);

  /**
   * Handle filter changes
   */
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      offset: 0 // Reset pagination
    }));
  }, []);

  /**
   * Handle search input
   */
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  /**
   * Handle search submit
   */
  const handleSearchSubmit = useCallback(() => {
    setFilters(prev => ({ ...prev, offset: 0 }));
  }, []);

  /**
   * Handle view change
   */
  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
    setFilters(prev => ({ ...prev, offset: 0 }));
  }, []);

  /**
   * Handle task creation
   */
  const handleTaskCreated = useCallback((newTask) => {
    if (currentScopeRef.current !== scopeKey) return;
    setTasks(prev => [newTask, ...prev]);
    setShowCreateModal(false);
    toast.success('Task created successfully');
    fetchTaskStats(); // Refresh stats
  }, [fetchTaskStats, scopeKey]);

  /**
   * Handle task update
   */
  const handleTaskUpdated = useCallback((updatedTask) => {
    if (currentScopeRef.current !== scopeKey) return;
    setTasks(prev => prev.map(task => 
      task.id === updatedTask.id ? updatedTask : task
    ));
    fetchTaskStats(); // Refresh stats
  }, [fetchTaskStats, scopeKey]);

  /**
   * Handle task deletion
   */
  const handleTaskDeleted = useCallback((taskId) => {
    if (currentScopeRef.current !== scopeKey) return;
    setTasks(prev => prev.filter(task => task.id !== taskId));
    toast.success('Task deleted successfully');
    fetchTaskStats(); // Refresh stats
  }, [fetchTaskStats, scopeKey]);

  /**
   * Handle load more (pagination)
   */
  const handleLoadMore = useCallback(() => {
    if (pagination.hasMore && !loading) {
      setFilters(prev => ({
        ...prev,
        offset: prev.offset + prev.limit
      }));
    }
  }, [pagination.hasMore, loading]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    setFilters(prev => ({ ...prev, offset: 0 }));
    fetchTaskStats();
  }, [fetchTaskStats]);

  /**
   * Handle export tasks
   */
  const handleExportTasks = useCallback(async () => {
    const requestScope = scopeKey;
    try {
      // Get all tasks for export
      const allTasks = await taskService.getTasks({ ...filters, limit: 10000, offset: 0 });
      if (currentScopeRef.current !== requestScope) return;
      
      // Convert to CSV
      const headers = ['Title', 'Status', 'Priority', 'Control ID', 'Assignee', 'Due Date', 'Created'];
      const rows = allTasks.tasks.map(task => [
        task.title,
        task.status,
        task.priority,
        task.control_id,
        task.assignee?.email || 'Unassigned',
        task.due_date ? new Date(task.due_date).toLocaleDateString() : '',
        new Date(task.created_at).toLocaleDateString()
      ]);
      
      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `e8-tasks-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Tasks exported successfully');
    } catch (err) {
      console.error('Failed to export tasks:', err);
      toast.error('Failed to export tasks');
    }
  }, [filters, scopeKey]);

  // Effects
  useEffect(() => {
    taskRequestRef.current += 1;
    statsRequestRef.current += 1;
    setTasks([]);
    setTaskStats(null);
    setPagination({ limit: 50, offset: 0, total: 0, hasMore: false });
    setShowCreateModal(false);
    setError(null);
    setLoading(Boolean(organisationId && engagementId));
    setFilters(previous => previous.offset === 0 && !previous.assessment_id
      ? previous
      : { ...previous, assessment_id: '', offset: 0 });
    if (organisationId && engagementId) taskService.setOrganisation(organisationId, engagementId);
  }, [organisationId, engagementId, scopeKey]);

  useEffect(() => {
    fetchTasks(true);
  }, [fetchTasks]);

  useEffect(() => {
    fetchTaskStats();
  }, [fetchTaskStats]);

  // View configuration
  const views = [
    { id: 'list', label: 'List View', icon: List },
    { id: 'my-tasks', label: 'My Tasks', icon: User },
    // { id: 'kanban', label: 'Kanban', icon: Grid }, // Phase 3
    // { id: 'calendar', label: 'Calendar', icon: Calendar }, // Phase 3
  ];

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">E8 Tasks</h1>
              <p className="text-gray-600 mt-1">Manage tasks for Essential Eight controls</p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <button
                onClick={handleExportTasks}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
              
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Task
              </button>
            </div>
          </div>
          
          {/* View Tabs */}
          <div className="mt-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {views.map((view) => {
                const Icon = view.icon;
                return (
                  <button
                    key={view.id}
                    onClick={() => handleViewChange(view.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center ${
                      currentView === view.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {view.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {taskStats && (
        <div className="px-6 py-4">
          <TaskStatsCards stats={taskStats} />
        </div>
      )}

      {/* Search and Filters */}
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium ${
              showFilters 
                ? 'bg-blue-50 text-blue-700 border-blue-300' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>
        
        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4">
            <TaskFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={() => handleFilterChange({
                status: '',
                priority: '',
                assignee_id: '',
                control_id: '',
                assessment_id: ''
              })}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-6 py-4">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {currentView === 'list' || currentView === 'my-tasks' ? (
          <TaskList
            key={scopeKey}
            tasks={tasks}
            loading={loading}
            pagination={pagination}
            onTaskUpdate={handleTaskUpdated}
            onTaskDelete={handleTaskDeleted}
            onLoadMore={handleLoadMore}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">This view will be implemented in Phase 3</p>
          </div>
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <TaskCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onTaskCreated={handleTaskCreated}
        />
      )}
    </div>
  );
};

export default TaskDashboard;
